import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

import { SteadilyEstimate, SteadilyEstimateResponse } from 'src/models/steadily.model';

/**
 * Real Steadily estimate proxy (gotidy PR #8538).
 *
 * `GET api/v1/customer/addresses/:address_id/insurance-estimate` returns the estimate
 * in the same `SteadilyEstimateResponse` shape `SteadilyMockService` produced, so the
 * insight pipeline consumes either source unchanged. The backend caches ~24h and matches
 * on our address id (`property_id`), so a per-address fetch is cheap after the first call.
 */
@Injectable({
  providedIn: 'root',
})
export class SteadilyService {

  private http = inject(HttpClient);

  async getEstimate(addressId: number): Promise<SteadilyEstimate | null> {
    const response = await firstValueFrom(
      this.http.get<SteadilyEstimateResponse>(
        `${environment.api_url}api/v1/customer/addresses/${addressId}/insurance-estimate`
      )
    );
    return response?.estimates?.[0] || null;
  }

  /**
   * Fetches estimates for many addresses at once. A single failing address must not blank
   * the whole insight feed, so failures are dropped and the caller falls back to mock pricing
   * for those addresses only.
   */
  async getEstimatesForAddresses(addressIds: number[]): Promise<Map<number, SteadilyEstimate>> {
    const estimates = new Map<number, SteadilyEstimate>();
    const results = await Promise.allSettled(
      addressIds.map(async id => ({ id, estimate: await this.getEstimate(id) }))
    );
    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value.estimate) {
        estimates.set(result.value.id, result.value.estimate);
      }
    });
    return estimates;
  }
}
